/**
 * Standardized error codes for consistent error handling across the application
 *
 * These codes provide a consistent way to identify and handle different types of errors
 * while maintaining type safety and avoiding enum-related issues.
 */

/**
 * Validation error codes
 */
export const VALIDATION_ERROR_CODES = {
  /** General validation failure */
  VALIDATION_ERROR: "VALIDATION_ERROR",
  /** Invalid input format or structure */
  INVALID_INPUT: "INVALID_INPUT",
  /** Required field is missing */
  MISSING_REQUIRED_FIELD: "MISSING_REQUIRED_FIELD",
  /** Field value is outside allowed range */
  VALUE_OUT_OF_RANGE: "VALUE_OUT_OF_RANGE",
  /** Field value has invalid format */
  INVALID_FORMAT: "INVALID_FORMAT",
  /** Field value is too long */
  VALUE_TOO_LONG: "VALUE_TOO_LONG",
  /** Field value is too short */
  VALUE_TOO_SHORT: "VALUE_TOO_SHORT",
  /** Field value contains invalid characters */
  INVALID_CHARACTERS: "INVALID_CHARACTERS",
} as const;

/**
 * Authentication error codes
 */
export const AUTHENTICATION_ERROR_CODES = {
  /** General authentication failure */
  AUTHENTICATION_ERROR: "AUTHENTICATION_ERROR",
  /** Authentication failed - invalid credentials */
  AUTHENTICATION_FAILED: "AUTHENTICATION_FAILED",
  /** Token is invalid or malformed */
  INVALID_TOKEN: "INVALID_TOKEN",
  /** Token has expired */
  TOKEN_EXPIRED: "TOKEN_EXPIRED",
  /** Token is missing from request */
  TOKEN_MISSING: "TOKEN_MISSING",
  /** Account is locked or disabled */
  ACCOUNT_LOCKED: "ACCOUNT_LOCKED",
  /** Account requires verification */
  ACCOUNT_UNVERIFIED: "ACCOUNT_UNVERIFIED",
  /** Too many failed login attempts */
  TOO_MANY_ATTEMPTS: "TOO_MANY_ATTEMPTS",
  /** Two-factor authentication required */
  TWO_FACTOR_REQUIRED: "TWO_FACTOR_REQUIRED",
  /** Two-factor authentication failed */
  TWO_FACTOR_FAILED: "TWO_FACTOR_FAILED",
} as const;

/**
 * Authorization error codes
 */
export const AUTHORIZATION_ERROR_CODES = {
  /** General authorization failure */
  AUTHORIZATION_ERROR: "AUTHORIZATION_ERROR",
  /** Access denied - insufficient permissions */
  AUTHORIZATION_DENIED: "AUTHORIZATION_DENIED",
  /** Resource access forbidden */
  ACCESS_FORBIDDEN: "ACCESS_FORBIDDEN",
  /** Insufficient permissions for operation */
  INSUFFICIENT_PERMISSIONS: "INSUFFICIENT_PERMISSIONS",
  /** Organization access denied */
  ORGANIZATION_ACCESS_DENIED: "ORGANIZATION_ACCESS_DENIED",
  /** Team access denied */
  TEAM_ACCESS_DENIED: "TEAM_ACCESS_DENIED",
  /** Role-based access denied */
  ROLE_ACCESS_DENIED: "ROLE_ACCESS_DENIED",
} as const;

/**
 * Not found error codes
 */
export const NOT_FOUND_ERROR_CODES = {
  /** General not found error */
  NOT_FOUND_ERROR: "NOT_FOUND_ERROR",
  /** Entity not found in database */
  ENTITY_NOT_FOUND: "ENTITY_NOT_FOUND",
  /** User not found */
  USER_NOT_FOUND: "USER_NOT_FOUND",
  /** Organization not found */
  ORGANIZATION_NOT_FOUND: "ORGANIZATION_NOT_FOUND",
  /** Team not found */
  TEAM_NOT_FOUND: "TEAM_NOT_FOUND",
  /** Resource not found */
  RESOURCE_NOT_FOUND: "RESOURCE_NOT_FOUND",
  /** File not found */
  FILE_NOT_FOUND: "FILE_NOT_FOUND",
  /** Route not found */
  ROUTE_NOT_FOUND: "ROUTE_NOT_FOUND",
  /** API endpoint not found */
  ENDPOINT_NOT_FOUND: "ENDPOINT_NOT_FOUND",
} as const;

/**
 * Conflict error codes
 */
export const CONFLICT_ERROR_CODES = {
  /** General conflict error */
  CONFLICT_ERROR: "CONFLICT_ERROR",
  /** Duplicate entity already exists */
  DUPLICATE_ENTITY: "DUPLICATE_ENTITY",
  /** Email already exists */
  EMAIL_ALREADY_EXISTS: "EMAIL_ALREADY_EXISTS",
  /** Username already exists */
  USERNAME_ALREADY_EXISTS: "USERNAME_ALREADY_EXISTS",
  /** Resource already exists */
  RESOURCE_ALREADY_EXISTS: "RESOURCE_ALREADY_EXISTS",
  /** Concurrent modification conflict */
  CONCURRENT_MODIFICATION: "CONCURRENT_MODIFICATION",
  /** Version conflict */
  VERSION_CONFLICT: "VERSION_CONFLICT",
  /** State conflict */
  STATE_CONFLICT: "STATE_CONFLICT",
} as const;

export const FRONTEND_ERROR_CODES = {
  /** General frontend error */
  FRONTEND_ERROR: "FRONTEND_ERROR",
} as const;

/**
 * Rate limit error codes
 */
export const RATE_LIMIT_ERROR_CODES = {
  /** General rate limit exceeded */
  RATE_LIMIT_EXCEEDED: "RATE_LIMIT_EXCEEDED",
  /** Too many requests */
  TOO_MANY_REQUESTS: "TOO_MANY_REQUESTS",
  /** API quota exceeded */
  QUOTA_EXCEEDED: "QUOTA_EXCEEDED",
  /** Resource exhausted */
  RESOURCE_EXHAUSTED: "RESOURCE_EXHAUSTED",
  /** Bandwidth limit exceeded */
  BANDWIDTH_LIMIT_EXCEEDED: "BANDWIDTH_LIMIT_EXCEEDED",
} as const;

/**
 * Network error codes
 */
export const NETWORK_ERROR_CODES = {
  /** General network error */
  NETWORK_ERROR: "NETWORK_ERROR",
  /** Connection timeout */
  CONNECTION_TIMEOUT: "CONNECTION_TIMEOUT",
  /** Request timeout */
  REQUEST_TIMEOUT: "REQUEST_TIMEOUT",
  /** Connection refused */
  CONNECTION_REFUSED: "CONNECTION_REFUSED",
  /** Network unreachable */
  NETWORK_UNREACHABLE: "NETWORK_UNREACHABLE",
} as const;

/**
 * Database error codes
 */
export const DATABASE_ERROR_CODES = {
  /** General database error */
  DATABASE_ERROR: "DATABASE_ERROR",
  /** Connection failed */
  DATABASE_CONNECTION_FAILED: "DATABASE_CONNECTION_FAILED",
  /** Query failed */
  DATABASE_QUERY_FAILED: "DATABASE_QUERY_FAILED",
  /** Transaction failed */
  DATABASE_TRANSACTION_FAILED: "DATABASE_TRANSACTION_FAILED",
  /** Constraint violation */
  DATABASE_CONSTRAINT_VIOLATION: "DATABASE_CONSTRAINT_VIOLATION",
  /** Deadlock detected */
  DATABASE_DEADLOCK: "DATABASE_DEADLOCK",
} as const;

/**
 * External service error codes
 */
export const EXTERNAL_SERVICE_ERROR_CODES = {
  /** General external service error */
  EXTERNAL_SERVICE_ERROR: "EXTERNAL_SERVICE_ERROR",
  /** Service unavailable */
  SERVICE_UNAVAILABLE: "SERVICE_UNAVAILABLE",
  /** Service timeout */
  SERVICE_TIMEOUT: "SERVICE_TIMEOUT",
  /** Service rate limit exceeded */
  SERVICE_RATE_LIMIT_EXCEEDED: "SERVICE_RATE_LIMIT_EXCEEDED",
  /** Service authentication failed */
  SERVICE_AUTHENTICATION_FAILED: "SERVICE_AUTHENTICATION_FAILED",
  /** Service authorization failed */
  SERVICE_AUTHORIZATION_FAILED: "SERVICE_AUTHORIZATION_FAILED",
  /** Service quota exceeded */
  SERVICE_QUOTA_EXCEEDED: "SERVICE_QUOTA_EXCEEDED",
} as const;

/**
 * Internal error codes
 */
export const INTERNAL_ERROR_CODES = {
  /** General internal error */
  INTERNAL_ERROR: "INTERNAL_ERROR",
  /** Unexpected error occurred */
  UNEXPECTED_ERROR: "UNEXPECTED_ERROR",
  /** System error */
  SYSTEM_ERROR: "SYSTEM_ERROR",
  /** Configuration error */
  CONFIGURATION_ERROR: "CONFIGURATION_ERROR",
  /** Feature not implemented */
  NOT_IMPLEMENTED: "NOT_IMPLEMENTED",
  /** Service temporarily unavailable */
  TEMPORARILY_UNAVAILABLE: "TEMPORARILY_UNAVAILABLE",
} as const;

/**
 * Business logic error codes
 */
export const BUSINESS_LOGIC_ERROR_CODES = {
  /** General business logic error */
  BUSINESS_LOGIC_ERROR: "BUSINESS_LOGIC_ERROR",
  /** Invalid business operation */
  INVALID_OPERATION: "INVALID_OPERATION",
  /** Business rule violation */
  BUSINESS_RULE_VIOLATION: "BUSINESS_RULE_VIOLATION",
  /** Workflow state invalid */
  INVALID_WORKFLOW_STATE: "INVALID_WORKFLOW_STATE",
  /** Operation not allowed in current state */
  OPERATION_NOT_ALLOWED: "OPERATION_NOT_ALLOWED",
} as const;

/**
 * Union type of all error codes for type safety
 */
export type ErrorCode =
  | (typeof VALIDATION_ERROR_CODES)[keyof typeof VALIDATION_ERROR_CODES]
  | (typeof AUTHENTICATION_ERROR_CODES)[keyof typeof AUTHENTICATION_ERROR_CODES]
  | (typeof AUTHORIZATION_ERROR_CODES)[keyof typeof AUTHORIZATION_ERROR_CODES]
  | (typeof NOT_FOUND_ERROR_CODES)[keyof typeof NOT_FOUND_ERROR_CODES]
  | (typeof CONFLICT_ERROR_CODES)[keyof typeof CONFLICT_ERROR_CODES]
  | (typeof RATE_LIMIT_ERROR_CODES)[keyof typeof RATE_LIMIT_ERROR_CODES]
  | (typeof NETWORK_ERROR_CODES)[keyof typeof NETWORK_ERROR_CODES]
  | (typeof DATABASE_ERROR_CODES)[keyof typeof DATABASE_ERROR_CODES]
  | (typeof EXTERNAL_SERVICE_ERROR_CODES)[keyof typeof EXTERNAL_SERVICE_ERROR_CODES]
  | (typeof INTERNAL_ERROR_CODES)[keyof typeof INTERNAL_ERROR_CODES]
  | (typeof BUSINESS_LOGIC_ERROR_CODES)[keyof typeof BUSINESS_LOGIC_ERROR_CODES];

/**
 * All error code constants grouped by category
 */
export const ERROR_CODES: {
  readonly VALIDATION: typeof VALIDATION_ERROR_CODES;
  readonly AUTHENTICATION: typeof AUTHENTICATION_ERROR_CODES;
  readonly AUTHORIZATION: typeof AUTHORIZATION_ERROR_CODES;
  readonly NOT_FOUND: typeof NOT_FOUND_ERROR_CODES;
  readonly CONFLICT: typeof CONFLICT_ERROR_CODES;
  readonly RATE_LIMIT: typeof RATE_LIMIT_ERROR_CODES;
  readonly NETWORK: typeof NETWORK_ERROR_CODES;
  readonly DATABASE: typeof DATABASE_ERROR_CODES;
  readonly EXTERNAL_SERVICE: typeof EXTERNAL_SERVICE_ERROR_CODES;
  readonly INTERNAL: typeof INTERNAL_ERROR_CODES;
  readonly BUSINESS_LOGIC: typeof BUSINESS_LOGIC_ERROR_CODES;
} = {
  VALIDATION: VALIDATION_ERROR_CODES,
  AUTHENTICATION: AUTHENTICATION_ERROR_CODES,
  AUTHORIZATION: AUTHORIZATION_ERROR_CODES,
  NOT_FOUND: NOT_FOUND_ERROR_CODES,
  CONFLICT: CONFLICT_ERROR_CODES,
  RATE_LIMIT: RATE_LIMIT_ERROR_CODES,
  NETWORK: NETWORK_ERROR_CODES,
  DATABASE: DATABASE_ERROR_CODES,
  EXTERNAL_SERVICE: EXTERNAL_SERVICE_ERROR_CODES,
  INTERNAL: INTERNAL_ERROR_CODES,
  BUSINESS_LOGIC: BUSINESS_LOGIC_ERROR_CODES,
} as const;
